import { cva } from 'class-variance-authority';
import { focusRing } from 'components/styles/focusRing';

/**
 * Geometry from Figma sets 2072:146115 (Tab) and 2072:148445 (Tabs). Colour and state live on
 * `.ds-tab` and `.ds-tabs` in the stylesheet, and none of it depends on the mode: panels,
 * navigation and filter share one drawing.
 *
 * ── Motion, and why there is so little of it ──────────────────────────────────────────────────────
 *
 * Switching tab is something a person does a few times a minute, not a few hundred, so it gets one
 * moving part and the rest is tint:
 *
 *   indicator   Slides to the chosen tab on `--motion-layout`: an element already on screen moving.
 *               Only when a POINTER chose. A keyboard change snaps, because keyboard-driven
 *               feedback never animates (docs/motion.md §4), and so does the first paint and a
 *               resize, which are not choices. Under reduced motion it snaps: the slide is movement,
 *               the position is the meaning, and the position stays.
 *   tab         Its tint and label colour on `--motion-control`, its press on `--motion-feedback`,
 *               as `Button` reads them. Hover is gated to real hover devices in the stylesheet.
 *   fades       `--motion-affordance`, the scroll-edge role the Dialog's fades already use.
 *   panel       None. A panel swap is a content change with no open/close lifecycle of its own, and
 *               an entrance keyed to mount would replay on every back-navigation.
 */
export const tabsRecipe = cva('ds-tabs group/tabs relative isolate flow-root box-border font-sans', {
  variants: {
    layout: {
      hug: 'w-full',
      equal: 'w-full',
    },
  },
  defaultVariants: { layout: 'hug' },
});

/**
 * The scrollport. `overflow-x: auto` forces `overflow-y` to clip as well, and the focus ring draws
 * 4px outside a tab, so the box takes 4px of padding on every side and hands it back as a negative
 * margin: the ring fits inside the scrollport and the strip still measures 44px. The root is
 * `flow-root` so those negative margins stay inside it: on a plain block they collapse through the
 * root and leave it 52px tall.
 *
 * The scrollbar is hidden because the edge fades are the affordance (Edo, 2026-09-29), and a native
 * bar would sit on the rail. The 4px overhang below the strip is transparent and takes the click
 * that would otherwise fall on the content under it, which is the price of a clipped-but-not-cut
 * ring and is 4px.
 */
export const tabsScrollerRecipe = cva([
  'relative z-[1] -m-1 overflow-x-auto overflow-y-hidden p-1',
  '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
]);

/**
 * Positioned, so a tab's `offsetLeft` is measured from it and the indicator can sit inside it.
 *
 * `hug` ends in a 4px spacer: a scrollport does not reliably count its own end padding as scrollable,
 * so without it the last tab could be scrolled flush against the edge and its focus ring clipped
 * there. Measured 2026-09-29 in Chromium: the last of six tabs ended 0.3px PAST the scrollport.
 */
export const tabsTrackRecipe = cva('relative flex min-w-full', {
  variants: {
    layout: {
      hug: "after:block after:w-1 after:shrink-0 after:content-['']",
      equal: 'w-full',
    },
  },
  defaultVariants: { layout: 'hug' },
});

/** The element that owns the tabs: a `tablist`, a group, or a `<nav>`. Static, never `relative`. */
export const tabsListRecipe = cva('flex gap-1', {
  variants: {
    layout: {
      hug: 'shrink-0',
      equal: 'w-full',
    },
  },
  defaultVariants: { layout: 'hug' },
});

export const tabRecipe = cva(
  [
    'ds-tab relative box-border inline-flex h-11 min-w-0 cursor-pointer items-center justify-center gap-2',
    'rounded-t-sm border-0 bg-transparent px-3 py-0 font-sans text-label-lg whitespace-nowrap no-underline select-none',
    '[-webkit-tap-highlight-color:transparent]',
    '[&[data-disabled]]:cursor-not-allowed',
    // A tab's own tint and label colour, then the shorter press, as `buttonRecipe` reads its two roles.
    'transition-[background-color,color] duration-[var(--motion-control)] ease-[var(--motion-control-ease)] motion-reduce:transition-none',
    '[&:not([data-disabled]):active]:duration-[var(--motion-feedback)]',
    '[&:not([data-disabled]):active]:ease-[var(--motion-feedback-ease)]',
    // The set draws the ring on all four corners, though the tab's bottom sits square on the rail.
    'focus-visible:z-[1] focus-visible:rounded-sm',
    focusRing('self'),
  ],
  {
    variants: {
      layout: {
        hug: 'shrink-0',
        equal: 'flex-1 basis-0',
      },
    },
    defaultVariants: { layout: 'hug' },
  }
);

/** Truncates rather than wraps: a label never takes a second line, and `equal` can run out of room. */
export const tabLabelRecipe = cva('truncate');

/**
 * The one moving part. A 1px-wide bar that is translated and scaled to sit under the chosen tab,
 * so the slide is `translate` and `scale` alone and never `left` or `width`.
 *
 * `useTabsStrip` writes `--ds-tab-x` and `--ds-tab-sx` and flips `data-ready` and `data-animate`.
 * `motion-safe:` rather than a `motion-reduce:` suppression, for the specificity reason
 * docs/motion.md §1 spells out: a state-qualified utility out-ranks a bare media query.
 */
export const tabsIndicatorRecipe = cva([
  'ds-tabs-indicator pointer-events-none absolute bottom-0 left-0 h-0.5 w-px origin-left opacity-0',
  'data-[ready]:opacity-100',
  'motion-safe:data-[animate]:transition-[translate,scale]',
  'motion-safe:data-[animate]:duration-[var(--motion-layout)]',
  'motion-safe:data-[animate]:ease-[var(--motion-layout-ease)]',
]);

/** Decorative, and it sits under the indicator, which is drawn over its last two pixels. */
export const tabsRailRecipe = cva('ds-tabs-rail pointer-events-none absolute inset-x-0 bottom-0 h-px');

/**
 * The scroll-edge fades. Shown only while something is hidden that way, from the root's
 * `data-overflow-*` attributes, so a strip that fits has none. 1px short of the bottom, so the rail
 * stays whole. 44px wide and pushed 4px past the edge: the scrollport overhangs the strip by its own
 * 4px of padding, and content scrolled into that overhang would otherwise show, uncovered, beside
 * the fade. The gradient holds those 4px opaque, so the fade itself is the drawn 40px.
 */
export const tabsFadeRecipe = cva(
  [
    'ds-tabs-fade pointer-events-none absolute top-0 bottom-px z-[2] w-11 opacity-0',
    'transition-opacity duration-[var(--motion-affordance)] ease-[var(--motion-affordance-ease)] motion-reduce:transition-none',
  ],
  {
    variants: {
      edge: {
        start: '-left-1 group-data-[overflow-start]/tabs:opacity-100',
        end: '-right-1 group-data-[overflow-end]/tabs:opacity-100',
      },
    },
  }
);
